import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { productoService, Producto, ProductoCreate, ProductoUpdate } from '../../services/productoService';
import { categoriaService } from '../../services/categoriaService';
import { useAuthStore } from '../../store/authStore';

interface ProductoFormProps {
  producto?: Producto | null;
  onClose: () => void;
}

const ProductoForm = ({ producto, onClose }: ProductoFormProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: categoriasData } = useQuery(
    'categorias',
    () => categoriaService.getAll(user?.tenantId)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<ProductoCreate | ProductoUpdate>({
    defaultValues: producto || {
      name: '',
      sku: '',
      description: '',
      tipo: 'producto',
      precioBase: 0,
      unidadMedida: 'unidad',
      manejaInventario: true
    }
  });

  const tipo = watch('tipo');

  useEffect(() => {
    if (producto) {
      reset({
        name: producto.name,
        sku: producto.sku,
        description: producto.description,
        tipo: producto.tipo,
        precioBase: producto.precioBase,
        categoriaId: producto.categoriaId,
        codigoBarras: producto.codigoBarras,
        unidadMedida: producto.unidadMedida,
        manejaInventario: producto.manejaInventario,
        costoPromedio: producto.costoPromedio,
        costoEstandar: producto.costoEstandar,
        imagenUrl: producto.imagenUrl,
        isActive: producto.isActive
      });
    }
  }, [producto, reset]);

  const createMutation = useMutation(productoService.create, {
    onSuccess: () => {
      toast.success('Producto creado correctamente');
      queryClient.invalidateQueries(['productos']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear producto');
    }
  });

  const updateMutation = useMutation(
    (data: ProductoUpdate) => productoService.update(producto!.id, data),
    {
      onSuccess: () => {
        toast.success('Producto actualizado correctamente');
        queryClient.invalidateQueries(['productos']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar producto');
      }
    }
  );

  const onSubmit = (data: ProductoCreate | ProductoUpdate) => {
    if (producto) {
      updateMutation.mutate(data as ProductoUpdate);
    } else {
      createMutation.mutate(data as ProductoCreate);
    }
  };

  const tipos = ['producto', 'servicio', 'combo'];
  const unidadesMedida = ['unidad', 'kg', 'litro', 'metro', 'caja', 'paquete'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {producto ? 'Editar Producto' : 'Nuevo Producto'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre *
              </label>
              <input
                {...register('name', { required: 'Nombre es requerido' })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SKU
              </label>
              <input
                {...register('sku')}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo *
              </label>
              <select
                {...register('tipo', { required: 'Tipo es requerido' })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {tipos.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoría
              </label>
              <select
                {...register('categoriaId', { valueAsNumber: true })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value={0}>Sin categoría</option>
                {categoriasData?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Precio Base *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                {...register('precioBase', { 
                  required: 'Precio base es requerido',
                  valueAsNumber: true,
                  min: { value: 0, message: 'Debe ser mayor o igual a 0' }
                })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.precioBase && (
                <p className="text-red-500 text-sm mt-1">{errors.precioBase.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Costo Promedio
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                {...register('costoPromedio', { valueAsNumber: true })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Costo Estándar
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                {...register('costoEstandar', { valueAsNumber: true })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código de Barras
              </label>
              <input
                {...register('codigoBarras')}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unidad de Medida
              </label>
              <select
                {...register('unidadMedida')}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {unidadesMedida.map((unidad) => (
                  <option key={unidad} value={unidad}>
                    {unidad}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {tipo === 'producto' && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('manejaInventario')}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Maneja Inventario</span>
              </label>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL de Imagen
            </label>
            <input
              type="url"
              {...register('imagenUrl')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {producto && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Activo</span>
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductoForm;

